import React from "react";
import { Popover } from "antd";

/**
 * Renders a coloured dot summarising whether a coin's deposit or withdraw
 * flow is live, with a hover popover that lists every supported chain.
 *
 * Colour rules:
 *   green  = coin.<flow>Enabled === true AND at least one network.is<Flow>Enable === true
 *   yellow = master flag on but every chain disabled (misconfiguration)
 *   red    = master flag off OR no networks configured
 *
 * Props:
 *   coin     — { depositEnabled?, withdrawEnabled?, symbol }
 *   networks — Array<{ chainName, networkKey?, isDepositEnable?, isWithdrawEnable?,
 *                      minDeposit?, minWithdraw?, maxWithdrawPerDay?,
 *                      withdrawFeeFlat?, withdrawFeePct? }>
 *   flow     — "deposit" | "withdraw"
 */
const CoinStatusIndicator = ({ coin, networks = [], flow = "deposit" }) => {
  const masterKey = flow === "withdraw" ? "withdrawEnabled" : "depositEnabled";
  const chainFlagKey = flow === "withdraw" ? "isWithdrawEnable" : "isDepositEnable";

  const masterOn = !!coin?.[masterKey];
  const list = Array.isArray(networks) ? networks : [];
  const anyChainOn = list.some((n) => n?.[chainFlagKey] === true);

  let color = "#e63c3c"; // red
  let bg = "rgba(242, 17, 54, 0.10)";
  let border = "rgba(242, 17, 54, 0.25)";
  let label = "Disabled";
  if (masterOn && anyChainOn) {
    color = "#26af48"; // green
    bg = "rgba(15, 183, 107, 0.12)";
    border = "rgba(15, 183, 107, 0.30)";
    label = "Enabled";
  } else if (masterOn && !anyChainOn && list.length > 0) {
    color = "#f39c12"; // amber — master on but every chain off
    bg = "rgba(255, 152, 0, 0.12)";
    border = "rgba(255, 152, 0, 0.30)";
    label = "Misconfigured";
  }

  const dot = (
    <span
      style={{
        display: "inline-block",
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: color,
        marginRight: 6,
        verticalAlign: "middle",
      }}
      title={label}
    />
  );

  const title = `${(coin?.symbol || "").toUpperCase()} — ${flow === "withdraw" ? "Withdraw" : "Deposit"}`;

  const content = (
    <div style={{ minWidth: 320, fontSize: 12 }}>
      <div style={{ marginBottom: 6, opacity: 0.7 }}>
        Master {masterKey}:{" "}
        <b style={{ color: masterOn ? "#52c41a" : "#ff4d4f" }}>
          {masterOn ? "ON" : "OFF"}
        </b>
      </div>
      {list.length === 0 && (
        <div style={{ opacity: 0.6 }}>No networks configured.</div>
      )}
      {list.length > 0 && (
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
              <th style={{ padding: "4px 6px" }}>Chain</th>
              <th style={{ padding: "4px 6px" }}>State</th>
              {flow === "withdraw" ? (
                <>
                  <th style={{ padding: "4px 6px", textAlign: "right" }}>Min</th>
                  <th style={{ padding: "4px 6px", textAlign: "right" }}>Max/day</th>
                  <th style={{ padding: "4px 6px", textAlign: "right" }}>Fee</th>
                </>
              ) : (
                <th style={{ padding: "4px 6px", textAlign: "right" }}>Min</th>
              )}
            </tr>
          </thead>
          <tbody>
            {list.map((n, idx) => {
              const on = n?.[chainFlagKey] === true;
              return (
                <tr key={n?.networkKey || n?.chainName || idx} style={{ borderBottom: "1px solid #f5f5f5" }}>
                  <td style={{ padding: "4px 6px" }}>{n?.chainName || n?.networkKey || "—"}</td>
                  <td style={{ padding: "4px 6px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "1px 8px",
                        borderRadius: 8,
                        background: on ? "#f6ffed" : "#fff1f0",
                        color: on ? "#52c41a" : "#ff4d4f",
                        border: `1px solid ${on ? "#b7eb8f" : "#ffa39e"}`,
                      }}
                    >
                      {on ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  {flow === "withdraw" ? (
                    <>
                      <td style={{ padding: "4px 6px", textAlign: "right" }}>
                        {n?.minWithdraw ?? "—"}
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "right" }}>
                        {n?.maxWithdrawPerDay ?? "—"}
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "right" }}>
                        {n?.withdrawFeeFlat ?? 0}
                        {n?.withdrawFeePct ? ` + ${n.withdrawFeePct}%` : ""}
                      </td>
                    </>
                  ) : (
                    <td style={{ padding: "4px 6px", textAlign: "right" }}>
                      {n?.minDeposit ?? "—"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <Popover title={title} content={content} placement="right" mouseEnterDelay={0.15}>
      <span
        style={{
          cursor: "help",
          display: "inline-flex",
          alignItems: "center",
          padding: "2px 10px",
          borderRadius: 999,
          background: bg,
          border: `1px solid ${border}`,
          fontSize: 12,
          fontWeight: 500,
          color,
          lineHeight: 1.4,
        }}
      >
        {dot}
        {label}
      </span>
    </Popover>
  );
};

export default CoinStatusIndicator;
